import { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent, useEffect, useRef, useState } from "react";

const MOBILE_NAV_BREAKPOINT = "(max-width: 920px)";
const NAVIGATOR_SEPARATOR_X_POSITION_STORAGE_KEY = "intelligo.admin.userprefs.navigator.separator.x-position";
const DEFAULT_LEFT_PANE_WIDTH = 280;
const MIN_LEFT_PANE_WIDTH = 220;
const MAX_LEFT_PANE_WIDTH = 520;

const getInitialLeftPaneWidth = () => {
  const savedValue = window.localStorage.getItem(NAVIGATOR_SEPARATOR_X_POSITION_STORAGE_KEY);
  const parsedValue = Number(savedValue);

  if (!Number.isFinite(parsedValue)) {
    return DEFAULT_LEFT_PANE_WIDTH;
  }

  return Math.min(MAX_LEFT_PANE_WIDTH, Math.max(MIN_LEFT_PANE_WIDTH, parsedValue));
};

export const useAdminShellState = () => {
  const [leftPaneWidth, setLeftPaneWidth] = useState(getInitialLeftPaneWidth);
  const [isDesktopNavHidden, setIsDesktopNavHidden] = useState(false);
  const [isLeftPaneCollapsed, setIsLeftPaneCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.matchMedia(MOBILE_NAV_BREAKPOINT).matches);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const resizeStartRef = useRef<{ startWidth: number; startX: number; } | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_NAV_BREAKPOINT);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsMobileNavOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    window.localStorage.setItem(NAVIGATOR_SEPARATOR_X_POSITION_STORAGE_KEY, String(leftPaneWidth));
  }, [leftPaneWidth]);

  useEffect(() => {
    if (!isMobileNavOpen) {
      document.body.classList.remove("is-mobile-admin-nav-open");
      return;
    }

    document.body.classList.add("is-mobile-admin-nav-open");
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileNavOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.classList.remove("is-mobile-admin-nav-open");
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (!isProfileMenuOpen) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Node)) {
        return;
      }
      if (!profileMenuRef.current?.contains(eventTarget)) {
        setIsProfileMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isProfileMenuOpen]);

  const startLeftPaneResize = (event: ReactMouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement>) => {
    if (isLeftPaneCollapsed) {
      return;
    }
    event.preventDefault();
    const startX = "touches" in event ? event.touches[0]?.clientX : event.clientX;
    if (typeof startX !== "number") {
      return;
    }
    resizeStartRef.current = { startWidth: leftPaneWidth, startX };
    document.body.classList.add("is-resizing-admin-pane");
    const handlePointerMove = (moveEvent: MouseEvent | TouchEvent) => {
      if (!resizeStartRef.current) {
        return;
      }
      const clientX = moveEvent instanceof MouseEvent ? moveEvent.clientX : moveEvent.touches[0]?.clientX;
      if (typeof clientX !== "number") {
        return;
      }
      const delta = clientX - resizeStartRef.current.startX;
      const nextWidth = Math.min(MAX_LEFT_PANE_WIDTH, Math.max(MIN_LEFT_PANE_WIDTH, resizeStartRef.current.startWidth + delta));
      setLeftPaneWidth(nextWidth);
    };
    const stopResize = () => {
      resizeStartRef.current = null;
      document.body.classList.remove("is-resizing-admin-pane");
      document.removeEventListener("mousemove", handlePointerMove);
      document.removeEventListener("mouseup", stopResize);
      document.removeEventListener("touchmove", handlePointerMove);
      document.removeEventListener("touchend", stopResize);
      document.removeEventListener("touchcancel", stopResize);
    };
    document.addEventListener("mousemove", handlePointerMove);
    document.addEventListener("mouseup", stopResize);
    document.addEventListener("touchmove", handlePointerMove, { passive: false });
    document.addEventListener("touchend", stopResize);
    document.addEventListener("touchcancel", stopResize);
  };

  return {
    isDesktopNavHidden,
    isLeftPaneCollapsed,
    isMobileNavOpen,
    isMobileViewport,
    isProfileMenuOpen,
    leftPaneWidth,
    profileMenuRef,
    setIsDesktopNavHidden,
    setIsLeftPaneCollapsed,
    setIsMobileNavOpen,
    setIsProfileMenuOpen,
    startLeftPaneResize
  };
};
